<script lang="ts" setup>
import { computed, ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useUiStore from '../stores/ui';
import useReaderStore from '../stores/reader';
import { type NavigationItem } from '../types';
import AnnotationContent from './annotations/AnnotationContent.vue';

type Props = {
  annotationItems?: NavigationItem[];
};

defineProps<Props>();

const { smAndDown: isMobile, width: screenWidth } = useDisplay();

const uiStore = useUiStore();
const { isAnnotationsDrawerVisible, topBarHeight, bottomBarHeight } = storeToRefs(uiStore);
const { toggleAnnotationsDrawer } = uiStore;

const { isAnnotationBtnsToolbar } = storeToRefs(useReaderStore());

const annotationDrawer = ref<HTMLElement | null>(null);

const drawerWidth = computed(() => (isMobile.value ? screenWidth.value : 420));

const drawerClasses = computed(() => {
  return [
    'annotation-drawer-custom',
    {
      'annotations-at-toolbar': isAnnotationBtnsToolbar.value,
    },
  ];
});

// @ts-ignore - this is the recommended way to import it - https://www.npmjs.com/package/@vueuse/integrations
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
const { activate, deactivate } = useFocusTrap(annotationDrawer, { allowOutsideClick: true });

const dynamicTopBarHeight = computed(() => `${topBarHeight.value}px`);
const dynamicBottomBarHeight = computed(() => `${bottomBarHeight.value}px`);

watch(isAnnotationsDrawerVisible, async (visible) => {
  if (visible) {
    await nextTick();
    activate();
  } else {
    deactivate();
  }
});
</script>

<template>
  <v-navigation-drawer
    data-testid="bp-annotations-drawer"
    :model-value="isAnnotationsDrawerVisible"
    :inert="!isAnnotationsDrawerVisible"
    :width="drawerWidth"
    :class="drawerClasses"
    location="right"
    floating
    temporary
    touchless
    @update:model-value="toggleAnnotationsDrawer"
  >
    <div ref="annotationDrawer">
      <AnnotationContent :annotation-items="annotationItems" @close-annotations="toggleAnnotationsDrawer(false)">
        <template #default="slotProps">
          <slot v-bind="slotProps" />
        </template>
      </AnnotationContent>
    </div>
  </v-navigation-drawer>
</template>

<style scoped lang="scss">
.annotation-drawer-custom {
  --bar-top-height: v-bind('dynamicTopBarHeight');
  --bar-bottom-height: calc(v-bind('dynamicBottomBarHeight') + env(safe-area-inset-bottom, 0px));

  padding-left: 16px;
  padding-right: 16px;

  &.annotations-at-toolbar {
    top: calc(var(--bar-top-height) + env(safe-area-inset-top, 0px)) !important;
    height: calc(100dvh - var(--bar-top-height) - var(--bar-bottom-height)) !important;
  }
}
</style>
